import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IRouteDefinitionParams, ISystem, ISystemRole,} from '../../../../index';
import {nsRoute} from "../../../../util/route";
import {createErrorMessage, getKnockoutThrottle, isSupported} from "../../../../util/misc";
import {getHash} from "../../../../constants/RouteDefinitions";
import {global, lm} from "../../../../appContext";
import {REQUEST_MESSAGE} from "../../../../constants/Language";
import {nsFa} from "../../../fa/fn";
import {REQUEST_THROTTLE, SYSTEM_MEGA_EADM} from "../../../../constants";
import {IRole, TRoleKey} from "./index";
import pubsub from "../../../../pubsub";
import {TableBasic} from "../../../table/basic";
import {ITableColumnDef, ITableDef} from "../../../table/index";
import {Selected} from "../../../table/selected";
import {nsSystem} from "../../fn";
import {storeObservable} from "../../../../util/sessionstore";
import {IParams as IEllipsisButtonParams} from "../../../button/ellipsis-dropdown"
import {ISearchObjectRouteOptions} from "@component/object";
import {withHashQuery} from "../../../../util/hashQuery";
import {getTabHash} from "../../../../util/pageShellRoute";
import {navigateToShellTab} from "../../../../util/pageShellTabRoutes";
import {getSearchBoxHeadingKey} from "../../../search/boxHeadingKey";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import i = nsFa.i;
import createRulesetWithRuleForObjectPermissionSystemRole = nsSystem.createRulesetWithRuleForObjectPermissionSystemRole;
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

export class viewModel extends ParsedRouteDataDecorator {

    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly searchkey: KnockoutObservable<string> = ko.observable('').extend(getKnockoutThrottle({rateLimit: REQUEST_THROTTLE}));
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly hasFocus: KnockoutObservable<boolean> = ko.observable(false);
    private readonly showAll: KnockoutObservable<boolean> = storeObservable('systemaccesssearch.role.show.all')
    private readonly sortcol: KnockoutObservable<keyof IRole> = ko.observable<keyof IRole>('name');

    private _data!: ISystem[];
    private _roles: KnockoutObservableArray<IRole> = ko.observableArray<IRole>([]);
    private selectedObjects: KnockoutObservableArray<IRole> = ko.observableArray<IRole>([]);
    private tableDef!: TableBasic<IRole>;
    private selectedDef!: Selected<IRole>;
    private readonly hitCount: KnockoutObservable<number> = ko.observable(0);

    /** White-box title key (`*.search`); distinct from route `label` used for tabs / page H1. */
    private readonly searchBoxHeading = ko.pureComputed(() => getSearchBoxHeadingKey(this.__routeDef));

    /** Heading badge: filtered hit count when searching / show-all; hidden when idle. */
    private readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.isReady() || (!this.showAll() && !this.searchkey())) {
            return null;
        }
        return this.hitCount();
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    private readonly searchBadgeWaiting = ko.pureComputed((): boolean => !this.isReady());

    constructor(params: IRouteDefinitionParams) {
        super(params);

        pubsub.on(this, 'collection.needs.refresh', this.init.bind(this));

        this.init();
    }

    private init() {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE})
        this.__requestRouteData!.request()
            .then((data: ISystem[]) => {
                this._data = data;
                const roles: IRole[] = data.reduce((arr: IRole[], o: ISystem) => {
                    const roles: IRole[] = o.roles.map((r: ISystemRole) => _.defaults(r, {$system: o}))
                    return arr.concat(roles)
                }, []);
                this._roles(roles);

                const def: ITableDef<IRole> = {
                    columns: [{
                        key: 'id',
                        searchable: true,
                        sortable: true,
                    }, {
                        getSortValue: o => o.name.toLowerCase(),
                        key: 'name',
                        label: 'permission',
                        searchable: true,
                        sortable: true,
                    }, {
                        key: 'roleenum',
                        label: 'external id',
                        searchable: true,
                        sortable: true,
                    }, {
                        key: 'priority',
                        getSortValue: o => o.$system.prioritybasedroles ? o.priority : -1, // -1 if priority is not relevant
                        searchable: true,
                        show: o => o.$system.prioritybasedroles,
                        sortable: true,
                    }, {
                        contentType: 'html',
                        getContent: (o, c, v) => isSupported(v as boolean),
                        getSortValue: o => o.$system.purgemanualdepartmentpermissions ? Number(o.purgemanualdepartmentpermissions) : -1, // -1 if purgemanualdepartmentpermissions is not relevant
                        key: 'purgemanualdepartmentpermissions',
                        label: 'remove manually assigned department permissions',
                        searchable: true,
                        show: (o, c, v) => o.$system.purgemanualdepartmentpermissions,
                        sortable: true,
                    }, {
                        key: '$system',
                        label: 'collection',
                        searchable: true,
                        sortable: true,
                        getContent: o => o.$system.name,
                        getSortValue: o => o.$system.name.toLowerCase(),
                    }, {
                        key: 'systemowner',
                        label: 'system owner',
                        getContent: o => o.systemowner || '—',
                        getTitle: o => o.systemowner,
                        sortable: true,
                    }, {
                        key: 'metadata',
                        getContent: o => o.metadata || '—',
                        getTitle: o => o.metadata,
                        sortable: true,
                    }, {
                        key: 'func' as any,
                        label: '',
                        validate: () => !this.selectedObjects().length,
                        contentType: 'component',
                        getComponent: (objRole, col, val) => {
                            const params: IEllipsisButtonParams = {
                                buttons: [{
                                    label: lm.t('search for users with this permission'),
                                    href: () => {
                                        const options: ISearchObjectRouteOptions = {
                                            $resetFilters: true,
                                            roleid: objRole.id,
                                            showAll: true,
                                            systemid: objRole.$system.id,
                                        };
                                        return withHashQuery(getHash('usersearch'), {
                                            resetFilters: options.$resetFilters,
                                            roleid: options.roleid,
                                            showAll: options.showAll,
                                            systemid: options.systemid,
                                        }) ?? ''
                                    },
                                }, {
                                    label: () => lm.t('Generate ruleset and message flow') ?? '',
                                    href: () => getHash('systemaccesswizardgenerate-rs-and-mf', {
                                        systemid: objRole.systemid,
                                        systemroleid: objRole.id,
                                    }) ?? '',
                                    validator: () => !objRole.$system.definitionreadonly,
                                }, {
                                    label: lm.t('wizard for assigning permissions') ?? '',
                                    href: () => getHash('wizardpermissions', {
                                        systemid: objRole.systemid,
                                        roleid: objRole.id,
                                    }) ?? '',
                                    validator: () => !objRole.$system.definitionreadonly,
                                }, {
                                    label: lm.t(`create ruleset for users with this permission`) ?? '',
                                    onClick: () => createRulesetWithRuleForObjectPermissionSystemRole(objRole),
                                }, {
                                    cls: 'btn-danger btn-ready-calm',
                                    label: lm.t('delete'),
                                    onClick: () => {
                                        global.dialogManager.addComponent({
                                            content: {
                                                name: 'component/systemaccess/dialog/purge-permission',
                                                params: {
                                                    permission: objRole,
                                                    system: objRole.$system,
                                                }
                                            },
                                            title: `${i('fa-trash-alt')} ((delete subject|s:((permission|ac)))) <span class="name">[${objRole.$system.name} ▷ ${objRole.name}]</span>`,
                                            onSuccess: () => this.init(),
                                            onError: () => this.init(),
                                        })
                                    },
                                    validator: () => objRole.$system.name != SYSTEM_MEGA_EADM && !objRole.$system.definitionreadonly,
                                }]
                            }
                            return {
                                name: 'component/button/ellipsis-dropdown',
                                params: params
                            }
                        }

                    }],
                    data: roles,
                    header: {
                        getContent: (col: ITableColumnDef<IRole>) => lm.t(col.label ?? col.key) ?? '',
                        getTitle: (col: ITableColumnDef<IRole>) => lm.t(col.label ?? col.key) ?? '',
                    },
                    navigation: {
                        pagenr: ko.observable(0),
                        totalresults: this.hitCount,
                    },
                    rows: {
                        getAttr: o => {
                            const ret: any = {}
                            if (this.selectedObjects().length) {
                                if (o.$system.name == SYSTEM_MEGA_EADM) {
                                    ret.tabIndex = -1
                                }
                            }
                            return ret
                        },
                        checkbox: {
                            isChecked: o => !!this.selectedObjects().find(_o => o.id == _o.id),
                            onToggle: o => {
                                this.selectedObjects().includes(o)
                                    ? this.selectedObjects.remove(o)
                                    : this.selectedObjects.push(o)
                            },
                            show: o => {
                                return o.$system.name != SYSTEM_MEGA_EADM && !o.$system.definitionreadonly
                            },
                        },
                        onClick: (o: IRole) => {
                            // Open the collection on the permissions tab (not bare shell / tab memory → /info).
                            const params = {id: o.$system.id};
                            if (this.selectedObjects()?.length) {
                                window.open(getTabHash('systemaccessroles', params) ?? '', '_blank');
                            } else {
                                navigateToShellTab('systemaccessdetails', 'systemaccessroles', params);
                            }
                        },
                    },
                    searchkey: ko.pureComputed((): string | null => {
                        if (this.showAll()) {
                            return ""
                        }
                        return this.searchkey() || null
                    }),
                    selected: this.selectedObjects,
                    sort: {ascending: ko.observable(false), sortColumn: this.sortcol},
                    translationKey: 'permission',
                };

                this.tableDef = new TableBasic<IRole>(def)

                def.operationsButton = {
                    mainButton: {
                        label: () => lm.t(`delete subject|s:((permission|ac|n:${this.selectedObjects().length}))`),
                        cls: 'btn-danger btn-ready-calm',
                        onClick: () => this.onDeleteClick(),
                    }
                }

                this.selectedDef = new Selected<IRole>(def)

                this.messageData(null);
                this.isReady(true);
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
    }


    private onDeleteClick() {
        const roles = this.selectedObjects();
        global.dialogManager.addComponent({
            content: {
                name: 'component/systemaccess/dialog/purge-permissions',
                params: {
                    roles: roles,
                }
            },
            title: `${i('fa-trash-alt')} ((delete subject|s:((permission|ac|n:${roles.length}))))`,
            onSuccess: () => {
                this.selectedObjects([])
            },
            onError: () => {
                this.selectedObjects([])
            },
        })
    }

}
